import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const CategoriesList = ({
  filteredCategories,
  onCategorySelect,
  activeCategory,
  totalCategoriesCount,
  navZone,
  navCategoryIndex,
  setNavCategoryIndex,
  setNavZone,
}) => {
  const allCategoriesList = ["All Categories", ...filteredCategories];

  // Client-side chunking to keep the DOM light and smooth
  const [displayCount, setDisplayCount] = useState(30);
  const observerRef = useRef(null);
  const observerTimeoutRef = useRef(null);

  // Reset display count when categories change
  useEffect(() => {
    setDisplayCount(30);
  }, [filteredCategories]);

  // Clean up observer timeout on unmount
  useEffect(() => {
    return () => {
      if (observerTimeoutRef.current) clearTimeout(observerTimeoutRef.current);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  // Keyboard navigation auto-expansion: if user scrolls with arrow keys near the end of the chunk, load more
  useEffect(() => {
    if (navZone === "sidebar" && navCategoryIndex >= displayCount - 3) {
      setDisplayCount((prev) => Math.max(prev, navCategoryIndex + 15));
    }
  }, [navCategoryIndex, navZone, displayCount]);

  // Intersection observer to load more when scroll reaches the last visible item
  const lastCategoryRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (!node) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            if (observerTimeoutRef.current) clearTimeout(observerTimeoutRef.current);
            observerTimeoutRef.current = setTimeout(() => {
              setDisplayCount((prev) => {
                if (prev >= allCategoriesList.length) return prev;
                return prev + 30;
              });
            }, 100);
          }
        },
        { threshold: 0.1 }
      );

      observerRef.current.observe(node);
    },
    [allCategoriesList.length]
  );

  return (
    <div className="overflow-x-auto pb-2 md:pb-0 scrollbar-thin scrollbar-thumb-slate-200">
      <ul className="flex md:flex-col space-x-2 md:space-x-0 md:space-y-1.5">
        {allCategoriesList.slice(0, displayCount).map((category, index) => {
          const categoryName =
            category === "All Categories"
              ? "All Categories"
              : category.newTitle;

          const categoryValue =
            category === "All Categories" ? "All" : category.newTitle;

          const isActive = activeCategory === categoryValue;

          const isFocused = navZone === "sidebar" && navCategoryIndex === index;

          const isLastVisible = index === Math.min(displayCount, allCategoriesList.length) - 1;

          return (
            <li
              ref={isLastVisible ? lastCategoryRef : null}
              key={category === "All Categories" ? "all" : category._id}
              data-nav-category={index}
              className={`shrink-0 rounded-xl cursor-pointer transition-all duration-200 ${
                isActive
                  ? "bg-teal-600 text-white shadow-md shadow-teal-600/10 scale-[1.02]"
                  : "bg-slate-50 hover:bg-slate-100/80 text-slate-700 hover:text-teal-600"
              } ${
                isFocused
                  ? isActive
                    ? "ring-2 ring-inset ring-white"
                    : "ring-2 ring-inset ring-teal-600 bg-teal-50 scale-[1.02]"
                  : ""
              }`}
              onClick={() => {
                setNavCategoryIndex(index);
                setNavZone("sidebar");
                onCategorySelect(categoryValue);
              }}
            >
              <button
                className="text-left w-full px-4 py-2 text-sm md:text-base font-semibold transition-colors duration-150 truncate"
                title={categoryName}
              >
                {categoryName}
              </button>
            </li>
          );
        })}

        {totalCategoriesCount === 0 && (
          <li className="pt-2 shrink-0 md:ms-0 flex justify-center">
            <Link
              to="/categories"
              className="flex items-center gap-1 text-xs font-bold text-teal-600 hover:text-teal-700 hover:underline transition py-1"
            >
              + Add Category
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
};

export default CategoriesList;
